/**
 * Blurp.ie - Cognitive Navigation System
 * "Air is not just for survival; it is the Instruction Set for human intelligence."
 */

import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MainLayout } from '@/components/layout';
import { LandingPage, WaitlistPage } from '@/components/landing';
import { RouteConfirmation } from '@/components/route';
import { LoginPage, SignupPage, ForgotPasswordPage, AuthGuard } from '@/auth';
import { PricingPage, BillingPage, UpgradeModal, setUpgradeModalHandler } from '@/subscription';
import { SocialHub } from '@/social';
import { PrivacyPolicy } from '@/components/legal/PrivacyPolicy';
import { IncognitoWarning } from '@/components/social/IncognitoWarning';
import { warnIfIncognito } from '@/utils/incognitoDetection';
import { initializeAuth, useSubscriptionStore } from '@/stores';
import { useNavigationStore } from '@/stores/useNavigationStore';
import type { FeatureKey, SubscriptionTier } from '@/types/subscription';
import '@/styles/globals.css';

// Create a client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Initialize auth on app load
initializeAuth();

// Warn if incognito mode detected
warnIfIncognito();

// Clear stale preview flags on fresh page load (not in-app SPA navigation)
// performance.navigation.type === 1 means reload, 0 means fresh load
if (typeof performance !== 'undefined' && performance.getEntriesByType('navigation').length > 0) {
  const navType = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming).type;
  if (navType === 'navigate' || navType === 'reload') {
    sessionStorage.removeItem('blurp_preview_mode');
    sessionStorage.removeItem('blurp_pending_route');
  }
}

function AppContent() {
  const location = useLocation();
  // Show map if navigated here with preview state, or if active navigation is running
  const [showApp, setShowApp] = useState(() => !!(location.state as any)?.previewRoute);

  // Upgrade modal state
  const [upgradeModal, setUpgradeModal] = useState<{
    isOpen: boolean;
    feature?: FeatureKey;
    targetTier?: SubscriptionTier;
    onDismiss?: () => void;
  }>({ isOpen: false });

  // Fetch exchange rates on mount
  const fetchExchangeRates = useSubscriptionStore((state) => state.fetchExchangeRates);

  useEffect(() => {
    fetchExchangeRates();
  }, [fetchExchangeRates]);

  // Set up global upgrade modal handler
  useEffect(() => {
    setUpgradeModalHandler((feature, targetTier, onDismiss) => {
      setUpgradeModal({ isOpen: true, feature, targetTier, onDismiss });
    });

    return () => setUpgradeModalHandler(null);
  }, []);

  // Check if navigation is active - if so, always show map
  const isNavigating = useNavigationStore((state) => state.isNavigating);

  const handleEnterApp = () => {
    setShowApp(true);
  };

  // Auto-show app when active navigation starts
  useEffect(() => {
    if (isNavigating && !showApp) setShowApp(true);
  }, [isNavigating, showApp]);


  return (
    <>
      {/* Incognito Mode Warning - Shows globally when private browsing detected */}
      <IncognitoWarning />

      <Routes>
        {/* Auth routes - accessible only when NOT logged in */}
        <Route
          path="/login"
          element={
            <AuthGuard requireAuth={false} redirectTo="/">
              <LoginPage />
            </AuthGuard>
          }
        />
        <Route
          path="/signup"
          element={
            <AuthGuard requireAuth={false} redirectTo="/">
              <SignupPage />
            </AuthGuard>
          }
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Subscription routes */}
        <Route path="/pricing" element={<PricingPage />} />
        <Route
          path="/billing"
          element={
            <AuthGuard requireAuth={true} redirectTo="/login">
              <BillingPage />
            </AuthGuard>
          }
        />

        {/* Social Hub - Internet of Scent */}
        <Route
          path="/social"
          element={
            <AuthGuard requireAuth={true} redirectTo="/login">
              <SocialHub />
            </AuthGuard>
          }
        />

        {/* Route Confirmation - AI Planning (requires sign-up) */}
        <Route path="/plan-route" element={
          <AuthGuard requireAuth={true} redirectTo="/signup">
            <RouteConfirmation />
          </AuthGuard>
        } />

        {/* Legal pages */}
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/* Internet of Scents Waitlist */}
        <Route path="/waitlist" element={<WaitlistPage />} />

        {/* Main app route */}
        <Route
          path="/"
          element={
            showApp ? (
              <div className="app-layout">
                <MainLayout />
              </div>
            ) : (
              <LandingPage onEnterApp={handleEnterApp} />
            )
          }
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        onClose={() => {
          const onDismiss = upgradeModal.onDismiss;
          setUpgradeModal({ isOpen: false });
          onDismiss?.();
        }}
        feature={upgradeModal.feature}
        targetTier={upgradeModal.targetTier}
      />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
